// Protein, Transcript...
import { add, map, reduce } from "ramda";

import { EXPRESSION_LANE_BAR_HEIGHT, EXPRESSION_LANE_GAP, STRAND_TYPE, TRACK_TYPE } from "@/settings/settings.js";

import { type Range } from "../../graphql.js";
import { SEQUENCE_TYPE_COLORS } from "../genome-browser-color.js";
import { createExpressionOverlayShapes } from "./shape-expression.js";
import { type ICreateDefaultShapeParams, type IFeatureTooltipData, TRACK_TOOLTIP_TYPE } from "./shape-feature.js";
import { type IPileupFeature } from "./track-pileup-factory.js";
import {
  type GenomeViewerShape,
  type GenomeViewerShapeInteractivity,
  type getShapeRenderer,
  renderAminoAcidSequence,
} from "./track-shapes-konva-renderers.js";

export interface ICreateMappingShapeParams extends ICreateDefaultShapeParams {
  renderMapping: ReturnType<typeof getShapeRenderer>["renderMapping"];
}

const createMappingShape = (params: ICreateMappingShapeParams): GenomeViewerShapeInteractivity => {
  const {
    feature,
    pileupParams,
    laneNumber,
    selected,
    renderMapping,
    renderArrow,
    callTooltip,
    expressionColoringConditionNameLookup,
    groups,
  } = params;

  const shapeColor =
    feature.feature.type === TRACK_TYPE.TRANSCRIPT_LOCATION
      ? SEQUENCE_TYPE_COLORS[feature.feature.sequenceTypeId]
      : SEQUENCE_TYPE_COLORS[2];

  const textHeight = pileupParams.hideLabels ? 0 : pileupParams.labelHeight;
  const shapeY =
    feature.lane *
      (pileupParams.featureHeight +
        pileupParams.lanePadding +
        laneNumber * (EXPRESSION_LANE_GAP + EXPRESSION_LANE_BAR_HEIGHT)) +
    (feature.lane + 1) * textHeight;

  const shape: GenomeViewerShape = renderMapping({
    start: feature.feature.region?.range.start, // feature.x
    stop: feature.feature.region?.range.stop, // feature.x + feature.width
    shapes: feature.mappings,
    fill: selected ? "#42affa" : shapeColor,
    y: shapeY,
    height: pileupParams.featureHeight,
    hasStroke: feature.feature.region.strand === STRAND_TYPE.MINUS,
    inclusiveStartPosition: true,
  });

  if (!pileupParams.hideLabels) {
    shape.text(
      feature.feature.name,
      undefined,
      undefined,
      (labelWidth) => !feature.startCut || feature.width >= labelWidth,
    );
  }

  // check if the sequence is longer/shorter than the summed up mappings
  if (checkAaSeqFitsMapping(feature) && pileupParams.showSeqBoxes) {
    renderAminoAcidSequence(feature, pileupParams, shapeY, selected, renderArrow);
  }

  if (groups) {
    shape.highlight(...[...groups.keys()].map((project) => project.color));
  }

  const shapeInteractivity: GenomeViewerShapeInteractivity = shape.interactive();

  callTooltip(shapeInteractivity.interactiveShape, shapeInteractivity.tooltipTargetShape, {
    uid: `${feature.feature.type}-${feature.feature.id}`,
    title: feature.feature.name?.length ? feature.feature.name : `id:${feature.feature.id}`,
    type: TRACK_TOOLTIP_TYPE.FEATURE,
    content: {
      pileupFeature: feature,
      groups,
      expressionColoringConditionNameLookup,
    } as IFeatureTooltipData,
  });

  if (feature.expressionParams?.length) {
    createExpressionOverlayShapes({ ...params, referenceShapeYPosition: shapeY });
  }

  return shapeInteractivity;
};

const checkAaSeqFitsMapping = (feature: IPileupFeature): boolean => {
  const sequence: string = feature.feature.sequence && feature.feature.sequence.replace(/[*]$/, "");
  const baseMappings: Range[] = feature.feature.mappings;

  if (baseMappings && sequence) {
    const mappedSize: number = reduce<number, number>(
      add,
      0,
      map((o) => o.stop - o.start + 1, baseMappings),
    );
    // we need to ignore the stop codon which is part of the mapping (-1)
    return mappedSize / 3 - 1 === sequence.length;
  }

  return false;
};

export { createMappingShape };
